export const SELECTION_SCHEMA_VERSION = 2

export interface SelectionState {
    type: SelectionType
    mode: SelectionMode
    activeSegSource: string | undefined
    freeSelection: FreeSelection
    savedFreeSelections: FreeSelectionsForCase
    originalSavedFreeSelections: FreeSelectionsForCase
    hierarchicalSelection: HierarchicalSelection
    savedHierarchicalSelections: HierarchicalSelectionsForCase
    originalSavedHierarchicalSelections: HierarchicalSelectionsForCase
    displayedOnScatterPlot: SelectionBase[]
    unsupervisedSelection: UnsupervisedSelection
    savedUnsupervisedSelections: HierarchicalSelectionsForCase
    originalSavedUnsupervisedSelections: HierarchicalSelectionsForCase
    resolution: number
}

export enum SelectionType {
    Free,
    Hierarchical,
    Unsupervised,
}

export enum SelectionName {
    Free = 'Selections',
    Hierarchical = 'Hierarchical Selections',
    Unsupervised = 'Unsupervised Selections',
}

export enum SelectionMode {
    Add,
    Subtract,
}

export interface SelectionBucket {
    segSource: string
    points: Set<number>
}

export interface SelectionBucketJson {
    segSource: string
    points: number[]
}

export interface SelectionBase {
    id: string
    name: string | undefined
    modified: boolean
    buckets: SelectionBucket[]
    annotationVisible: boolean
    annotationColor: string // hex string
    annotationOpacity: number // 0..1
}

export interface FreeSelection extends SelectionBase {
    type: SelectionType.Free
}

export interface HierarchicalSelectionNode extends SelectionBase {
    children: HierarchicalSelectionNode[] | undefined
}
export interface UnsupervisedSelectionNode extends SelectionBase {
    children: UnsupervisedSelectionNode[] | undefined
}

export interface HierarchicalSelectionRoot {
    id: string
    name: string | undefined
    modified: boolean
    type: SelectionType.Hierarchical | SelectionType.Unsupervised
    children: HierarchicalSelectionNode[] | undefined
    /** Intensity-map segSources locked at create time; gating cannot expand beyond these. */
    segSources: string[]
}

export interface UnsupervisedSelectionRoot {
    id: string
    name: string | undefined
    modified: boolean
    type: SelectionType.Unsupervised | SelectionType.Hierarchical
    children: UnsupervisedSelectionNode[] | undefined
    /** Intensity-map segSources locked at create time; gating cannot expand beyond these. */
    segSources: string[]
}

export interface SelectionLayerInfo extends SelectionBase {
    type: SelectionType
    rootName: string | undefined
}

export interface HierarchicalSelection extends HierarchicalSelectionRoot {
    selectedHierarchyName: string | undefined
    unsavedLevel: SelectionBase
}

export interface UnsupervisedSelection extends UnsupervisedSelectionRoot {
    selectedUnsupervisedName: string | undefined
    unsavedLevel: SelectionBase
}

export type FreeSelectionsForCase = Map<string, FreeSelection>
export type HierarchicalSelectionsForCase = Map<string, HierarchicalSelectionRoot>
export type UnsupervisedSelectionsForCase = Map<string, UnsupervisedSelectionRoot>

export type SelectionsForCaseList = SelectionBase[]

export interface SelectionTreeItem {
    groupName: string | undefined
    level: number
    name: string | undefined
    size: number
    id: string
    path: string[]
}

export interface JobSelectionRef {
    id: string
    type: 1 | 2 | 3
    path?: string[]
}

/** True when both are hierarchical nodes in the same tree and one path is an ancestor of the other. */
export function areHierarchicalAncestorRelated(
    a: { type: number; path?: string[]; rootName?: string },
    b: { type: number; path?: string[]; rootName?: string }
): boolean {
    if (a.type !== 2 && a.type !== 3) return false
    if (b.type !== 2 && b.type !== 3) return false
    if (!a.rootName || a.rootName !== b.rootName) return false
    const pathA = a.path ?? []
    const pathB = b.path ?? []
    return isStrictPathPrefix(pathA, pathB) || isStrictPathPrefix(pathB, pathA)
}

function isStrictPathPrefix(prefix: string[], full: string[]): boolean {
    if (prefix.length >= full.length) return false
    return prefix.every((segment, i) => segment === full[i])
}

/** True if any pair of hierarchical selections is ancestor/descendant. */
export function hasHierarchicalAncestryConflict(
    selections: { type: number; path?: string[]; rootName?: string }[]
): boolean {
    for (let i = 0; i < selections.length; i++) {
        for (let j = i + 1; j < selections.length; j++) {
            if (areHierarchicalAncestorRelated(selections[i], selections[j])) {
                return true
            }
        }
    }
    return false
}

export function generateSelectionId(): string {
    return crypto.randomUUID()
}

export function createEmptyBucket(segSource: string): SelectionBucket {
    return { segSource, points: new Set() }
}

export function copyBuckets(buckets: SelectionBucket[]): SelectionBucket[] {
    return buckets.map(b => ({ segSource: b.segSource, points: new Set(b.points) }))
}

export function getBucket(
    buckets: SelectionBucket[],
    segSource: string
): SelectionBucket | undefined {
    return buckets.find(b => b.segSource === segSource)
}

export function getOrCreateBucket(buckets: SelectionBucket[], segSource: string): SelectionBucket {
    const existing = getBucket(buckets, segSource)
    if (existing) return existing
    const created = createEmptyBucket(segSource)
    buckets.push(created)
    return created
}

/** Ensure a bucket exists for each segSource (empty points when none selected). */
export function ensureBucketsForSegSources(
    buckets: SelectionBucket[],
    segSources: string[]
): SelectionBucket[] {
    const next = copyBuckets(buckets)
    for (const segSource of segSources) {
        if (segSource) getOrCreateBucket(next, segSource)
    }
    return next
}

export function mergePointsIntoBuckets(
    buckets: SelectionBucket[],
    segSource: string,
    points: number[],
    mode: SelectionMode
): SelectionBucket[] {
    const next = copyBuckets(buckets)
    const bucket = getOrCreateBucket(next, segSource)
    if (mode === SelectionMode.Add) {
        points.forEach(p => bucket.points.add(p))
    } else {
        points.forEach(p => bucket.points.delete(p))
    }
    return next
}

export function clearBucket(buckets: SelectionBucket[], segSource: string): SelectionBucket[] {
    return buckets.map(b =>
        b.segSource === segSource ? { ...b, points: new Set<number>() } : b
    )
}

export function clearAllBuckets(buckets: SelectionBucket[]): SelectionBucket[] {
    return buckets.map(b => ({ ...b, points: new Set<number>() }))
}

export function getPointsForSegSource(
    selection: { buckets: SelectionBucket[] },
    segSource?: string
): Set<number> {
    if (segSource) {
        return new Set(getBucket(selection.buckets, segSource)?.points ?? [])
    }
    return getAllPointsFromBuckets(selection.buckets)
}

/** Buckets for the currently editable (unsaved) selection, across all selection types. */
export function getEditableSelectionBuckets(state: SelectionState): SelectionBucket[] {
    if (state.type === SelectionType.Free) {
        return state.freeSelection.buckets
    }
    if (state.type === SelectionType.Hierarchical) {
        return state.hierarchicalSelection.unsavedLevel.buckets
    }
    return state.unsupervisedSelection.unsavedLevel.buckets
}

/** Total selected segment count across all buckets (segment ids may overlap between maps). */
export function getEditableSelectionSize(state: SelectionState): number {
    return getEditableSelectionBuckets(state).reduce((n, b) => n + b.points.size, 0)
}

export function getTotalPointsSize(selection: { buckets: SelectionBucket[] }): number {
    return getAllPointsFromBuckets(selection.buckets).size
}

export function getDistinctSegSources(selection: { buckets: SelectionBucket[] }): string[] {
    return [...new Set(selection.buckets.filter(b => b.points.size > 0).map(b => b.segSource))]
}

/**
 * True when every non-empty bucket belongs to `segSource` (no points on other maps).
 * Empty footprint buckets for other segSources are ignored.
 */
export function selectionBelongsOnlyToSegSource(
    selection: { buckets: SelectionBucket[] },
    segSource: string | undefined
): boolean {
    if (!segSource) return false
    const sources = getDistinctSegSources(selection)
    return sources.length === 1 && sources[0] === segSource
}

/** All bucket segSources, including empty buckets (saved intensity-map footprint). */
export function getAllBucketSegSources(selection: { buckets: SelectionBucket[] }): string[] {
    return [...new Set(selection.buckets.map(b => b.segSource).filter(Boolean))]
}

export function selectionSpansMultipleSegSources(selection: { buckets: SelectionBucket[] }): boolean {
    return getDistinctSegSources(selection).length > 1
}

export function sameStringSet(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false
    const bSet = new Set(b)
    return a.every(x => bSet.has(x))
}

/** Collect every bucket segSource under a hierarchical/unsupervised tree (incl. empty buckets). */
export function collectSegSourcesFromHierarchicalTree(
    root:
        | HierarchicalSelectionRoot
        | UnsupervisedSelectionRoot
        | HierarchicalSelectionNode
        | UnsupervisedSelectionNode
): string[] {
    const sources = new Set<string>()
    const walk = (node: HierarchicalSelectionNode | UnsupervisedSelectionNode): void => {
        for (const bucket of node.buckets) {
            if (bucket.segSource) sources.add(bucket.segSource)
        }
        for (const child of node.children ?? []) {
            walk(child)
        }
    }
    if ('buckets' in root) {
        walk(root as HierarchicalSelectionNode)
    } else {
        for (const child of root.children ?? []) {
            walk(child)
        }
    }
    return [...sources].sort((a, b) => a.localeCompare(b))
}

/**
 * Locked intensity-map footprint for a hierarchical/unsupervised root.
 * Prefers explicit create-time `segSources`; falls back to tree buckets for older saves.
 */
export function getHierarchicalRootSegSources(
    root: HierarchicalSelectionRoot | UnsupervisedSelectionRoot
): string[] {
    if (root.segSources?.length) {
        return [...root.segSources].sort((a, b) => a.localeCompare(b))
    }
    return collectSegSourcesFromHierarchicalTree(root)
}

export function getAllPointsFromBuckets(buckets: SelectionBucket[]): Set<number> {
    const points = new Set<number>()
    for (const bucket of buckets) {
        for (const point of bucket.points) {
            points.add(point)
        }
    }
    return points
}

/** Stable key for a segment within a specific intensity map (matches plotPointKey). */
export function selectionPointKey(segSource: string, segmentId: number): string {
    return `${segSource}:${segmentId}`
}

export function getPointKeysFromBuckets(buckets: SelectionBucket[]): Set<string> {
    const keys = new Set<string>()
    for (const bucket of buckets) {
        for (const point of bucket.points) {
            keys.add(selectionPointKey(bucket.segSource, point))
        }
    }
    return keys
}

export function getPointKeysForSelection(selection: { buckets: SelectionBucket[] }): Set<string> {
    return getPointKeysFromBuckets(selection.buckets)
}

export const getAllChildrenPointKeysInRootOrNode = (
    selection:
        | HierarchicalSelectionRoot
        | HierarchicalSelectionNode
        | UnsupervisedSelectionRoot
        | UnsupervisedSelectionNode
): Set<string> => {
    const keys = new Set<string>()
    if (selection.children) {
        for (const child of selection.children) {
            for (const key of getAllOwnAndChildrenPointKeysInNode(child)) {
                keys.add(key)
            }
        }
    }
    return keys
}

export const getAllOwnAndChildrenPointKeysInNode = (
    selection: HierarchicalSelectionNode | UnsupervisedSelectionNode
): Set<string> => {
    const keys = getPointKeysForSelection(selection)
    if (selection.children) {
        for (const child of selection.children) {
            for (const key of getAllOwnAndChildrenPointKeysInNode(child)) {
                keys.add(key)
            }
        }
    }
    return keys
}

export function bucketsEqual(a: SelectionBucket[], b: SelectionBucket[]): boolean {
    const aSources = new Set(a.map(x => x.segSource))
    const bSources = new Set(b.map(x => x.segSource))
    if (aSources.size !== bSources.size) return false
    for (const segSource of aSources) {
        if (!bSources.has(segSource)) return false
        const aPoints = getBucket(a, segSource)!.points
        const bPoints = getBucket(b, segSource)!.points
        if (
            aPoints.size !== bPoints.size ||
            ![...aPoints].every(p => bPoints.has(p))
        ) {
            return false
        }
    }
    return true
}

export function mergeBucketsIntoTarget(
    target: SelectionBucket[],
    source: SelectionBucket[]
): SelectionBucket[] {
    const next = copyBuckets(target)
    for (const src of source) {
        const bucket = getOrCreateBucket(next, src.segSource)
        for (const point of src.points) {
            bucket.points.add(point)
        }
    }
    return next
}

export function removeBucketsFromTarget(
    target: SelectionBucket[],
    source: SelectionBucket[]
): SelectionBucket[] {
    const next = copyBuckets(target)
    for (const src of source) {
        const bucket = getBucket(next, src.segSource)
        if (!bucket) continue
        for (const point of src.points) {
            bucket.points.delete(point)
        }
    }
    return next
}

export const findSelectionNodeInRoot = (
    selection: HierarchicalSelectionRoot,
    name: string | undefined
): HierarchicalSelectionNode | null => {
    if (selection.children) {
        for (const child of selection.children) {
            const result = findSelectionNodeInNode(child, name)
            if (result) {
                return result
            }
        }
    }
    return null
}

export const findSelectionNodeByIdInRoot = (
    selection: HierarchicalSelectionRoot,
    id: string
): HierarchicalSelectionNode | null => {
    if (selection.children) {
        for (const child of selection.children) {
            const result = findSelectionNodeByIdInNode(child, id)
            if (result) {
                return result
            }
        }
    }
    return null
}

const findSelectionNodeInNode = (
    selection: HierarchicalSelectionNode,
    name: string | undefined
): HierarchicalSelectionNode | null => {
    if (selection.name === name) {
        return selection
    }
    if (selection.children) {
        for (const child of selection.children) {
            const result = findSelectionNodeInNode(child, name)
            if (result) {
                return result
            }
        }
    }
    return null
}

const findSelectionNodeByIdInNode = (
    selection: HierarchicalSelectionNode,
    id: string
): HierarchicalSelectionNode | null => {
    if (selection.id === id) {
        return selection
    }
    if (selection.children) {
        for (const child of selection.children) {
            const result = findSelectionNodeByIdInNode(child, id)
            if (result) {
                return result
            }
        }
    }
    return null
}

export const findNodePathInRoot = (
    selection: HierarchicalSelectionRoot,
    targetName: string | undefined
): string[] => {
    if (!targetName) return []
    const path: string[] = []
    const found = findNodePathInChildren(selection.children ?? [], targetName, path)
    return found ? path : []
}

const findNodePathInChildren = (
    children: HierarchicalSelectionNode[],
    targetName: string,
    path: string[]
): boolean => {
    for (const child of children) {
        path.push(child.name!)
        if (child.name === targetName) return true
        if (child.children && findNodePathInChildren(child.children, targetName, path)) {
            return true
        }
        path.pop()
    }
    return false
}

export const findParentSelectionNodeInRoot = (
    selection: HierarchicalSelectionRoot,
    name: string | undefined
): HierarchicalSelectionNode | null => {
    if (selection.children) {
        for (const child of selection.children) {
            if (child.name === name) {
                return null
            }
            const parent = findParentSelectionNodeInNode(child, name)
            if (parent) {
                return parent
            }
        }
    }
    return null
}

const findParentSelectionNodeInNode = (
    selection: HierarchicalSelectionNode,
    name: string | undefined
): HierarchicalSelectionNode | null => {
    if (selection.children) {
        for (const child of selection.children) {
            if (child.name === name) {
                return selection
            }
            const parent = findParentSelectionNodeInNode(child, name)
            if (parent) {
                return parent
            }
        }
    }
    return null
}

export const getAllChildrenSelectionTreeItems = (
    selection:
        | HierarchicalSelectionRoot
        | HierarchicalSelectionNode
        | UnsupervisedSelectionRoot
        | UnsupervisedSelectionNode,
    level?: number,
    parentPath: string[] = []
): SelectionTreeItem[] => {
    let result: SelectionTreeItem[] = []
    level = level ?? 1
    for (const child of selection.children ?? []) {
        if (child.name) {
            const path = [...parentPath, child.name]
            result.push({
                level,
                name: child.name,
                size: getTotalPointsSize(child),
                groupName: selection.name,
                id: child.id,
                path,
            })
        }
        result = result.concat(
            getAllChildrenSelectionTreeItems(child, level + 1, [
                ...parentPath,
                ...(child.name ? [child.name] : []),
            ])
        )
    }
    return result
}

export const getSelectionTreeItemLevel = (
    selection:
        | HierarchicalSelectionRoot
        | HierarchicalSelectionNode
        | UnsupervisedSelectionRoot
        | UnsupervisedSelectionNode,
    name: string,
    level?: number
): number => {
    level = level ?? 1
    for (const child of selection.children ?? []) {
        if (child.name == name) {
            return level
        }
        const childLevel = getSelectionTreeItemLevel(child, name, level + 1)
        if (childLevel > 0) return childLevel
    }
    return 0
}

export const getAllChildrenPointsInRootOrNode = (
    selection:
        | HierarchicalSelectionRoot
        | HierarchicalSelectionNode
        | UnsupervisedSelectionRoot
        | UnsupervisedSelectionNode,
    segSource?: string
): Set<number> => {
    const points = new Set<number>()
    if (selection.children) {
        for (const child of selection.children) {
            const childPoints = getAllOwnAndChildrenPointsInNode(child, segSource)
            for (const point of childPoints) {
                points.add(point)
            }
        }
    }
    return points
}

export const getAllOwnPointsInNode = (
    selection: HierarchicalSelectionNode,
    segSource?: string
): Set<number> => {
    return getPointsForSegSource(selection, segSource)
}

export const getAllOwnAndChildrenPointsInNode = (
    selection: HierarchicalSelectionNode,
    segSource?: string
): Set<number> => {
    const points = getPointsForSegSource(selection, segSource)
    if (selection.children) {
        for (const child of selection.children) {
            const childPoints = getAllOwnAndChildrenPointsInNode(child, segSource)
            for (const point of childPoints) {
                points.add(point)
            }
        }
    }
    return points
}

export const getAllChildren = (
    selection:
        | HierarchicalSelectionRoot
        | HierarchicalSelectionNode
        | UnsupervisedSelectionRoot
        | UnsupervisedSelectionNode
): HierarchicalSelectionNode[] => {
    if (selection.children) {
        return selection.children.reduce((children, child) => {
            const newChildren = getAllChildren(child)
            if (child) {
                newChildren.push(child)
            }
            return children.concat(newChildren)
        }, [] as HierarchicalSelectionNode[])
    }
    return []
}

export const SELECTION_LAYER_LIST_THRESHOLD = 100

export const getListableSelectionNodes = (
    selection: HierarchicalSelectionRoot | UnsupervisedSelectionRoot
): HierarchicalSelectionNode[] => {
    const all = getAllChildren(selection)
    if (all.length <= SELECTION_LAYER_LIST_THRESHOLD) {
        return all
    }
    return selection.children ?? []
}

export const addChildToSelectionRoot = (
    selectionRoot: HierarchicalSelectionRoot,
    newChild: HierarchicalSelectionNode,
    targetName: string | undefined
): boolean => {
    if (!targetName) {
        if (!selectionRoot.children) {
            selectionRoot.children = []
        }
        selectionRoot.children.push(newChild)
        return true
    }
    if (selectionRoot.children) {
        for (const child of selectionRoot.children) {
            if (addChildToSelectionNode(child, newChild, targetName)) {
                return true
            }
        }
    }
    return false
}

const addChildToSelectionNode = (
    selectionNode: HierarchicalSelectionNode,
    newChild: HierarchicalSelectionNode,
    targetName: string
): boolean => {
    if (selectionNode.name === targetName) {
        if (!selectionNode.children) {
            selectionNode.children = []
        }
        selectionNode.children.push(newChild)
        return true
    }
    if (selectionNode.children) {
        for (const child of selectionNode.children) {
            if (addChildToSelectionNode(child, newChild, targetName)) {
                return true
            }
        }
    }
    return false
}

export const removeChildrenFromSelectionRoot = (
    selectionRoot: HierarchicalSelectionRoot,
    name: string
): void => {
    if (selectionRoot.children) {
        selectionRoot.children = selectionRoot.children.filter(child => {
            if (child.name === name) {
                return false
            }
            removeChildrenFromSelectionNode(child, name)
            return true
        })
    }
}

const removeChildrenFromSelectionNode = (
    selectionNode: HierarchicalSelectionNode,
    name: string
): void => {
    if (selectionNode.children) {
        const copiedNode = [...selectionNode.children]
        const children: HierarchicalSelectionNode[] = []

        copiedNode.forEach(child => {
            if (child.name === name) {
                children.push(...Array.from(child.children ?? []))
            } else {
                removeChildrenFromSelectionNode(child, name)
                children.push(child)
            }
        })
        selectionNode.children = children
    }
}

export const removeChildrenFromSelectionRootRecursively = (
    selectionRoot: HierarchicalSelectionRoot,
    name: string
): void => {
    if (selectionRoot.children) {
        selectionRoot.children = selectionRoot.children.filter(child => {
            if (child.name === name) {
                return false
            }
            removeChildrenFromSelectionNodeRecursively(child, name)
            return true
        })
    }
}

const removeChildrenFromSelectionNodeRecursively = (
    selectionNode: HierarchicalSelectionNode,
    name: string
): void => {
    if (selectionNode.children) {
        selectionNode.children = selectionNode.children.filter(child => {
            if (child.name === name) {
                return false
            }
            removeChildrenFromSelectionNodeRecursively(child, name)
            return true
        })
    }
}
